import { entity, legalPaths } from '../entity';
import {
  P, H3, Ul, Table, Callout, Ext, Tok,
} from '../blocks';
import type { LegalDoc } from '../types';

// Aviso de Privacidad (español).
//
// Para usuarios en México este texto es el aviso de privacidad integral exigido
// por el art. 15 de la LFPDPPP y prevalece sobre la versión en inglés. Mantener
// en paridad con privacy.en.tsx: mismos ids de sección, mismos hechos.

export const privacyEs: LegalDoc = {
  lang: 'es',
  title: 'Aviso de Privacidad',
  updatedLabel: `Última actualización: ${entity.effectiveDate}`,
  contentsLabel: 'Contenido',
  altHref: legalPaths.privacy,
  altLabel: 'View in English',
  sections: [
    {
      id: 'who-we-are',
      heading: '1. Quiénes somos y qué cubre este aviso',
      body: (
        <>
          <P>
            Tribys es una aplicación para encontrar grupos pequeños de personas con
            intereses afines y conocerlas en la vida real. Este aviso explica qué
            datos personales recabamos, para qué, a quién los transferimos, cuánto
            tiempo los conservamos y qué puedes hacer al respecto.
          </P>
          <P>
            El <strong>responsable</strong> del tratamiento de tus datos personales
            es <strong><Tok value={entity.legalName} /></strong>, sociedad de
            responsabilidad limitada constituida en{' '}
            <Tok value={entity.formedIn} />, Estados Unidos, con domicilio en{' '}
            <Tok value={entity.address} />. Puedes contactarnos en{' '}
            <Tok value={entity.privacyEmail} />.
          </P>
          <P>
            Este aviso cubre la aplicación móvil Tribys, el sitio tribys.com y
            nuestros servicios de backend. No cubre los servicios de terceros que
            tú decidas conectar o abrir desde Tribys, los cuales se rigen por sus
            propias políticas.
          </P>
          <P>
            Existen dos documentos relacionados. El{' '}
            <a href={legalPaths.biometricEs}>
              Aviso sobre Datos Biométricos y Datos de Salud del Consumidor
            </a>{' '}
            detalla la verificación de identidad y la comparación facial, y se
            publica por separado porque la legislación de Washington e Illinois así
            lo exige. Los Términos del Servicio y las Normas de la Comunidad
            regulan la conducta, no los datos.
          </P>
        </>
      ),
    },
    {
      id: 'age',
      heading: '2. Debes tener 18 años o más',
      body: (
        <>
          <P>
            Tribys es para personas adultas. Debes tener al menos 18 años para
            crear una cuenta y te pedimos tu fecha de nacimiento durante el
            registro para comprobarlo. Todos los grupos tienen también una edad
            mínima de 18 años.
          </P>
          <P>
            No recabamos conscientemente datos de menores de 18 años. Si crees que
            un menor tiene una cuenta, escribe a{' '}
            <Tok value={entity.privacyEmail} /> y la eliminaremos.
          </P>
        </>
      ),
    },
    {
      id: 'collect',
      heading: '3. Qué datos personales recabamos',
      body: (
        <>
          <P>
            Recabamos únicamente lo que la aplicación necesita para funcionar. En
            Tribys no hay ninguna red publicitaria, ningún SDK de analítica ni
            ningún intermediario de datos — véase la sección 8.
          </P>
          <Table
            head={['Categoría', 'En concreto', 'Origen']}
            rows={[
              [
                'Cuenta y acceso',
                'Nombre de usuario, correo electrónico y/o número de teléfono, y una contraseña (almacenada únicamente como hash: nunca conservamos la contraseña en sí). Si accedes con Google o Apple, el identificador de cuenta y el correo electrónico que ese proveedor nos devuelve. Códigos de un solo uso de seis dígitos cuando los solicitas.',
                'Tú, o el proveedor de acceso que elijas',
              ],
              [
                'Perfil',
                'Nombre completo, fecha de nacimiento, género, una biografía breve, estado civil, tipo de personalidad, idiomas que hablas y al menos cinco intereses. Si conectas Spotify, tu nombre para mostrar y los artistas que elijas mostrar.',
                'Tú',
              ],
              [
                'Verificación de identidad (opcional)',
                'Si decides verificarte: el nombre, la fecha de nacimiento y el género que constan en tu identificación oficial, qué tipo de documento era, y una sola imagen de referencia de tu rostro. No conservamos la imagen de tu documento de identidad, ni su número, ni ningún número personal o de identificación nacional que contenga, ni el video de verificación.',
                'Tú, mediante nuestro encargado de verificación',
              ],
              [
                'Ubicación',
                'Una única ubicación actual: la posición de tu dispositivo o un punto que tú coloques en el mapa. Conservamos solo el último punto, no un historial ni un recorrido. También derivamos una ubicación aproximada a nivel de ciudad a partir de tu dirección IP.',
                'Tu dispositivo, o tú',
              ],
              [
                'Lo que creas',
                'Mensajes de chat, títulos y descripciones de grupos, nombres, descripciones, direcciones y coordenadas exactas de lugares, fotos que subes y el texto de cualquier reporte que envíes.',
                'Tú',
              ],
              [
                'Tus vínculos',
                'Amistades y solicitudes de amistad, membresías e invitaciones a grupos, y la lista de cuentas que has bloqueado.',
                'Tú y otros miembros',
              ],
              [
                'Dispositivo',
                'Un token de notificaciones push, un identificador de instalación y si usas iOS o Android.',
                'Tu dispositivo',
              ],
              [
                'Compras',
                'Qué producto compraste, cuándo, y los identificadores de transacción que nos dan Apple o Google. Nunca vemos ni almacenamos los datos de tu tarjeta: el pago lo gestionan íntegramente Apple y Google.',
                'Apple, Google',
              ],
              [
                'Registros técnicos',
                'Tu identificador de cuenta, dirección IP y marcas de tiempo de las solicitudes a nuestros servidores, conservados por seguridad y depuración.',
                'Automáticamente',
              ],
              [
                'Datos derivados',
                'Tu edad (calculada a partir de tu fecha de nacimiento), los resultados de la moderación automatizada de contenido, y los datos de coincidencia y ordenamiento que determinan qué grupos aparecen en la búsqueda y qué notificaciones recibes.',
                'Generados por nosotros',
              ],
            ]}
          />
        </>
      ),
    },
    {
      id: 'sensitive',
      heading: '4. Datos personales sensibles',
      body: (
        <>
          <P>
            Tratamos tres tipos de datos que reciben protección reforzada. Los
            identificamos expresamente como <strong>sensibles</strong>: tu{' '}
            <strong>ubicación precisa</strong> y tus <strong>datos
            biométricos</strong>.
          </P>
          <H3>Ubicación precisa</H3>
          <P>
            La ubicación es el centro de Tribys: la aplicación es un mapa y
            encontrar un grupo cerca de ti es su razón de ser. Por eso conviene que
            sepas exactamente cómo funciona:
          </P>
          <Ul
            items={[
              'Leemos tu ubicación solo mientras la aplicación está abierta y en primer plano. Nunca te rastreamos en segundo plano y la aplicación no solicita el permiso de ubicación en segundo plano.',
              'Enviamos una posición actualizada a nuestros servidores como máximo cada 15 minutos, y solo si te has desplazado más de unos 500 metros.',
              'Guardamos un único punto actual, que se sobrescribe en cada actualización. No construimos un historial de ubicación.',
              'Si niegas el permiso de ubicación, la aplicación sigue funcionando: puedes explorar sin ubicación o colocar un punto en el mapa para indicar aproximadamente dónde quieres buscar.',
              'Los demás miembros nunca ven tus coordenadas. Ven una distancia redondeada («a menos de 1 km», «a 6 km») y nada más.',
            ]}
          />
          <P>
            Los lugares son la excepción, y de forma deliberada: un lugar es un
            sitio público, y su nombre, dirección y coordenadas exactas son
            visibles para cualquiera, incluso en la web pública. No crees un lugar
            en tu domicilio.
          </P>
          <H3>Identidad y datos biométricos</H3>
          <P>
            La verificación es opcional y puedes omitirla. Si la usas, tratamos una
            imagen de tu rostro y comparamos con ella las fotos de perfil
            posteriores. Al ser datos biométricos, cuentan con su propio aviso:
            consulta el{' '}
            <a href={legalPaths.biometricEs}>
              Aviso sobre Datos Biométricos y Datos de Salud del Consumidor
            </a>
            .
          </P>
          <H3>Lo que tus intereses pueden revelar</H3>
          <P>
            Los intereses son de texto libre y los miembros pueden crear los suyos.
            Un interés o el nombre de un grupo que elijas podría sugerir algo sobre
            tus creencias, tu ideología, tu salud o tu orientación sexual. Queremos
            ser directos sobre cómo lo manejamos:
          </P>
          <Ul
            items={[
              'Usamos los intereses únicamente para sugerirte grupos y para ordenar los resultados de búsqueda. No los analizamos para inferir tu salud, religión, ideología u orientación sexual, ni elaboramos perfiles de ese tipo.',
              'No vendemos ni compartimos tus intereses, ni ningún otro dato, con fines publicitarios.',
              'Pero los intereses que pongas en tu perfil son visibles para otros miembros y aparecen en las páginas públicas de los grupos. Si un interés es algo que preferirías no publicar, no lo agregues.',
            ]}
          />
        </>
      ),
    },
    {
      id: 'use',
      heading: '5. Para qué usamos tus datos',
      body: (
        <>
          <Ul
            items={[
              <><strong>Para operar tu cuenta:</strong> iniciar sesión, verificar tu correo o teléfono, mantener tu sesión activa y restaurar tu cuenta si cambias de opinión sobre eliminarla.</>,
              <><strong>Para mostrarte grupos y lugares cerca de ti:</strong> búsqueda por zona del mapa y distancia. En una cuenta gratuita la búsqueda se limita a unos 10 km a tu alrededor; con Pro se amplía a unos 80 km.</>,
              <><strong>Para permitirte comunicarte:</strong> chat directo y de grupo, solicitudes de amistad, invitaciones y las notificaciones correspondientes.</>,
              <><strong>Para avisarte de grupos nuevos relevantes:</strong> cuando se crea un grupo público, buscamos miembros dentro de unos 80 km cuya edad, género e intereses encajen, y les enviamos una notificación. Puedes desactivarlo por tipo de notificación en Ajustes.</>,
              <><strong>Para mantener Tribys seguro:</strong> revisiones automatizadas de fotos y textos, atención de reportes y bloqueos, y aplicación de suspensiones. Véase la sección 6.</>,
              <><strong>Para gestionar compras:</strong> validar comprobantes con Apple o Google, dar seguimiento a tu suscripción y otorgar recompensas por referidos.</>,
              <><strong>Para mantener el servicio en funcionamiento y seguro:</strong> límites de solicitudes, prevención de abusos, monitoreo de errores y depuración.</>,
              <><strong>Para cumplir obligaciones legales:</strong> conservar registros contables y atender requerimientos legítimos de autoridad.</>,
            ]}
          />
          <P>
            No usamos tus datos para entrenar modelos de aprendizaje automático ni
            los enviamos a ningún servicio de inteligencia artificial de propósito
            general.
          </P>
        </>
      ),
    },
    {
      id: 'automated',
      heading: '6. Decisiones automatizadas y cómo llegar a una persona',
      body: (
        <>
          <P>
            Algunas decisiones en Tribys se toman de forma automatizada, sin que
            una persona las revise primero. Tienes derecho a saber cuáles son.
          </P>
          <Ul
            items={[
              <><strong>Moderación de fotos.</strong> Cada foto de perfil y de lugar se revisa automáticamente antes de publicarse. Una foto se rechaza si se detecta contenido sexual o inseguro, si una foto de perfil no contiene un rostro detectable o si, estando verificado, el rostro no coincide con tu imagen de verificación. La aplicación te indica cuál de estos casos ocurrió.</>,
              <><strong>Moderación de textos.</strong> Tu nombre, biografía, títulos y descripciones de grupos, datos de lugares y cualquier interés nuevo que crees se revisan en busca de contenido tóxico al guardarlos, y se rechazan si superan un umbral de confianza. Los mensajes privados de chat <em>no</em> pasan por esta revisión.</>,
              <><strong>Coincidencias y notificaciones.</strong> Quién recibe aviso de un grupo nuevo se decide automáticamente a partir de la ubicación, la edad, el género y los intereses, como se describe en la sección 5.</>,
              <><strong>Ordenamiento de búsqueda.</strong> El orden de los resultados se calcula automáticamente, y quien crea un grupo puede pagar para mejorar su posición.</>,
            ]}
          />
          <Callout>
            Si una decisión automatizada te afectó y consideras que fue incorrecta,
            escribe a <Tok value={entity.privacyEmail} /> y una persona la
            revisará.
          </Callout>
          <P>
            Las suspensiones no son automáticas: las emite una persona del equipo
            de moderación, con un motivo declarado, y pueden ser temporales o
            permanentes. No existe un sistema automático de faltas acumuladas.
          </P>
        </>
      ),
    },
    {
      id: 'share',
      heading: '7. Con quién compartimos datos (transferencias)',
      body: (
        <>
          <P>
            No vendemos tus datos personales. Sí utilizamos otras empresas para
            operar Tribys, y estas tratan tus datos únicamente para prestarnos
            servicios, como <strong>encargados</strong>, conforme a nuestras
            instrucciones. Estas remisiones no requieren tu consentimiento.
          </P>
          <P>
            Los nombramos en lugar de describirlos de forma vaga, porque deberías
            poder ver exactamente quién participa. Esta lista es exacta a la fecha
            que aparece al inicio de esta página. Podemos añadir o sustituir a un
            proveedor conforme evolucione el servicio y, cuando lo hagamos,
            actualizaremos esta lista y la fecha; si algún proveedor te importa,
            vuelve a consultarla.
          </P>
          <Table
            head={['Quién', 'Qué hace', 'Qué recibe']}
            rows={[
              [
                'Amazon Web Services',
                'Almacenamiento, análisis de imágenes, análisis de texto, envío de correo y SMS, y nuestra red de distribución de imágenes',
                'Fotos e imágenes de verificación; el texto de tu nombre, biografía y datos de grupos y lugares; tu correo o teléfono y los códigos que te enviamos',
              ],
              [
                'Google Cloud',
                'Alojamiento de nuestro backend y almacenamiento de secretos de configuración',
                'Todo lo que pasa por la API, más registros de solicitudes con tu identificador de cuenta y tu dirección IP',
              ],
              ['Neon', 'Nuestra base de datos administrada', 'La base de datos en su conjunto'],
              [
                'Upstash',
                'Mensajería en tiempo real entre nuestros servidores y límites de solicitudes',
                'Cargas de eventos en vivo y claves de límite que pueden incluir tu correo o teléfono',
              ],
              [
                'Didit',
                'Verificación de identidad',
                'Tu documento de identidad, selfie y video de verificación. Se detalla en el aviso biométrico',
              ],
              [
                'Google (Firebase) y Apple',
                'Entrega de notificaciones push',
                'El token de tu dispositivo y la notificación misma, que incluye el nombre de quien envía y hasta 120 caracteres de un mensaje de chat',
              ],
              ['Google Maps', 'Mostrar mapas en la aplicación y el sitio web', 'La zona del mapa que se visualiza'],
              [
                'Sentry',
                'Monitoreo de errores y rendimiento',
                'Tu identificador de cuenta y detalles técnicos del error. Desactivamos su recolección de datos personales y eliminamos las direcciones IP',
              ],
              [
                'Slack',
                'Nuestro canal interno de moderación',
                'Cuando envías un reporte: tu nombre de usuario, identificador de cuenta, correo y teléfono, junto con el reporte',
              ],
              [
                'Klipy',
                'El selector de GIF y stickers en el chat',
                'Lo que escribes en el buscador de GIF, enviado desde tu dispositivo directamente a ellos',
              ],
              [
                'Google Workspace',
                'Nuestro correo corporativo',
                'Lo que nos envíes cuando nos contactes, incluida cualquier solicitud de privacidad o de eliminación, ya que llegan por correo electrónico',
              ],
              ['Spotify', 'La conexión opcional con Spotify', 'Autorización para leer tus artistas más escuchados, si la conectas'],
              [
                'Apple y Google',
                'Acceso, y todos los pagos como comercio responsable',
                'Tokens de acceso y, en las compras, todo lo relativo a la transacción',
              ],
            ]}
          />
          <P>
            También divulgaremos datos cuando la ley lo exija —un requerimiento
            legal válido o una orden judicial— o cuando sea realmente necesario
            para proteger la seguridad de alguna persona o defender nuestros
            derechos. Si Tribys llegara a venderse o fusionarse, los datos se
            transferirían con la operación y lo indicaríamos aquí previamente.
          </P>
          <H3>Datos que salen a través de tu propio dispositivo</H3>
          <P>
            Algunas funciones entregan datos a software que no controlamos, a
            petición tuya:
          </P>
          <Ul
            items={[
              'Agregar un grupo a tu calendario escribe su título, hora y ubicación en la cuenta de calendario que use tu dispositivo, que puede sincronizarlo con Google, Apple o tu empresa.',
              'Abrir un lugar en una aplicación de mapas transmite sus coordenadas a esa aplicación.',
              'Compartir un enlace lo envía a la aplicación que elijas.',
            ]}
          />
        </>
      ),
    },
    {
      id: 'no-sale',
      heading: '8. No vendemos tus datos y no hay publicidad ni analítica',
      body: (
        <>
          <Callout>
            <strong>
              Nunca hemos vendido datos personales y no los compartimos para
              publicidad conductual entre contextos.
            </strong>{' '}
            Tribys no contiene ningún SDK de publicidad, de analítica ni de
            atribución o seguimiento de instalaciones, y no recabamos ningún
            identificador publicitario.
          </Callout>
          <P>
            Es una decisión arquitectónica deliberada, no solo una promesa. En la
            aplicación no hay Google Analytics, Firebase Analytics, Mixpanel,
            Amplitude, Segment, PostHog, AppsFlyer, Adjust ni Branch.
          </P>
          <P>
            Tampoco usamos tus datos para elaborar perfiles con efectos jurídicos o
            similarmente significativos, ni realizamos publicidad dirigida de
            ningún tipo.
          </P>
        </>
      ),
    },
    {
      id: 'retention',
      heading: '9. Cuánto tiempo conservamos los datos',
      body: (
        <Table
          head={['Datos', 'Plazo']}
          rows={[
            ['Cuenta y perfil', 'Mientras exista tu cuenta. Si la eliminas, los depuramos al concluir el periodo de gracia de 30 días descrito en la sección 12.'],
            ['Datos de verificación', 'Nuestro encargado de verificación elimina su copia —incluidos tu documento y el video— a los 6 meses. Nuestro propio calendario de conservación y destrucción consta en el aviso biométrico.'],
            ['Mensajes de chat', 'Mientras exista la conversación. Al eliminar tu cuenta, el contenido de tus mensajes se vacía, pero el registro del mensaje permanece para no destruir la conversación de la otra persona. Los chats de grupo se cierran 3 días después de la reunión.'],
            ['Fotos', 'Mientras estén en uso. Las imágenes sin usar o archivadas se eliminan a los 12 meses. Una carga que nunca termina de procesarse se elimina en aproximadamente un día.'],
            ['Ubicación', 'Un único punto actual, sobrescrito en cada actualización y borrado al eliminar tu cuenta.'],
            ['Archivos de exportación de datos', 'El enlace de descarga funciona 1 hora; el archivo se elimina automáticamente a los 7 días.'],
            ['Registros de pago', 'Se conservan tras la eliminación de la cuenta, durante el tiempo que exijan las obligaciones fiscales, contables y de prevención de fraude: en general alrededor de 7 años.'],
            ['Reportes, bloqueos y suspensiones', 'Se conservan tras la eliminación de la cuenta, porque borrarlos permitiría que una cuenta suspendida o abusiva se reiniciara, y podemos necesitarlos para defender una reclamación legal.'],
            ['Grupos, lugares, membresías, amistades y registros de referidos', 'Se conservan, porque otros miembros dependen de ellos. Una vez depurado el registro de tu cuenta, ya no te identifican.'],
            ['Preferencias y registros de actividad', 'Tus ajustes de chat, posiciones de lectura, notificaciones, preferencias de notificación, intereses e idiomas se conservan tras la eliminación. Son de baja sensibilidad y, una vez depurado el registro de tu cuenta, quedan vinculados únicamente a un identificador anonimizado.'],
            ['Registros del servidor', 'Una ventana breve y rotativa por seguridad y depuración, actualmente 30 días.'],
            ['Tokens de notificaciones', 'Se eliminan al cerrar sesión, al eliminar tu cuenta o cuando el servicio de notificaciones nos informa que el token dejó de ser válido.'],
          ]}
        />
      ),
    },
    {
      id: 'security',
      heading: '10. Cómo protegemos tus datos',
      body: (
        <>
          <Ul
            items={[
              'Todo se cifra en tránsito (TLS 1.2 o superior) y en reposo.',
              'Los tokens que conservamos de otros servicios —tu conexión con Spotify, el token de Apple que guardamos para poder revocarlo cuando te vas— se cifran por separado con AES-256-GCM.',
              'Las fotos y las imágenes de verificación se guardan en almacenamiento privado no listable públicamente; las cargas no se sirven desde nuestra CDN hasta haber superado la moderación.',
              'Nuestros servidores se autentican ante otras nubes con credenciales federadas de corta duración. No almacenamos claves de acceso permanentes en ningún sitio.',
              'Cada servicio del backend opera con los permisos mínimos necesarios y solo puede leer los secretos que declara.',
              'Las herramientas administrativas están restringidas a un rango de IP aprobado y a cuentas corporativas.',
              'Las contraseñas se almacenan únicamente como hash. Nadie en Tribys puede leer tu contraseña.',
            ]}
          />
          <P>
            Ningún sistema es completamente seguro. Si detectamos una vulneración
            que afecte tus datos, te lo notificaremos y avisaremos a las
            autoridades competentes conforme a la ley.
          </P>
        </>
      ),
    },
    {
      id: 'controls',
      heading: '11. Controles dentro de la aplicación',
      body: (
        <>
          <P>
            Casi todo lo que puedas querer hacer, puedes hacerlo por tu cuenta, sin
            pedírnoslo:
          </P>
          <Ul
            items={[
              <><strong>Descargar tus datos.</strong> Ajustes → Exportar mis datos genera un archivo JSON con los datos personales que tenemos sobre ti.</>,
              <><strong>Eliminar tu cuenta.</strong> Ajustes → Eliminar cuenta. La sección 12 explica exactamente qué implica.</>,
              <><strong>Editar o corregir tu perfil</strong> en cualquier momento. Ten en cuenta que, al verificar tu identidad, tu nombre, fecha de nacimiento y género quedan fijados a lo que indique tu identificación.</>,
              <><strong>Desactivar notificaciones</strong> de forma individual, por tipo, en Ajustes → Preferencias de notificaciones.</>,
              <><strong>Bloquear y reportar</strong> a otros miembros. El bloqueo es mutuo: ninguno de los dos ve el perfil completo del otro ni los grupos a los que se une.</>,
              <><strong>Silenciar una conversación</strong> o quitarla de tu propio dispositivo.</>,
              <><strong>Controlar la ubicación.</strong> Niega o revoca el permiso en los ajustes de tu dispositivo y sigue usando la aplicación, o define tu zona con un punto en el mapa.</>,
              <><strong>Omitir la verificación de identidad</strong> por completo. Es opcional.</>,
            ]}
          />
          <P>
            Si prefieres que actuemos por ti, o quieres ejercer un derecho que no
            tiene un botón, escribe a <Tok value={entity.privacyEmail} />. Es
            gratuito y no te trataremos de forma distinta por solicitarlo.
          </P>
        </>
      ),
    },
    {
      id: 'deletion',
      heading: '12. Qué ocurre al eliminar tu cuenta',
      body: (
        <>
          <P>
            Cuando eliminas tu cuenta la programamos para su supresión en 30 días y
            la ocultamos de inmediato a todas las demás personas. Durante esos 30
            días puedes volver a iniciar sesión y restaurarla. Después, la
            supresión se ejecuta automáticamente y es irreversible.
          </P>
          <P>En ese momento:</P>
          <Ul
            items={[
              'Se borran tu correo electrónico, número de teléfono, contraseña y cualquier vínculo con cuentas de Google o Apple, y tu nombre de usuario se sustituye por un identificador anónimo.',
              'Se borran tu nombre, fecha de nacimiento, género, biografía, estado civil y tipo de personalidad.',
              'Se borra tu ubicación almacenada.',
              'Se elimina tu registro de verificación, tus fotos de perfil se eliminan de nuestro almacenamiento y tu imagen de verificación se elimina de nuestro almacenamiento.',
              'Instruimos a nuestro encargado de verificación para que elimine su copia de tus datos de verificación.',
              'Si accediste con Apple, revocamos el token de acceso de Apple para que el vínculo quede cortado también en Apple.',
              'Se eliminan tu conexión con Spotify y sus tokens almacenados.',
              'Se vacía el contenido de tus mensajes de chat.',
            ]}
          />
          <P>
            Queremos ser precisos en un punto de forma deliberada: no eliminamos el
            registro base de la cuenta. Queda despojado de todo lo que te
            identifica y marcado como anonimizado, pero permanece, porque los
            grupos, lugares y conversaciones en los que participan otras personas
            apuntan a él. Eliminarlo por completo destruiría contenido de otros
            miembros y su parte de las conversaciones compartidas.
          </P>
          <P>
            Como se indica en la sección 9, los registros de pago y los reportes,
            bloqueos y suspensiones también sobreviven a la eliminación: los
            primeros porque estamos legalmente obligados a conservar registros
            contables, y los segundos porque, de lo contrario, eliminar una cuenta
            sería una forma de borrar una suspensión o un antecedente de seguridad.
          </P>
        </>
      ),
    },
    {
      id: 'where',
      heading: '13. Dónde se almacenan tus datos',
      body: (
        <>
          <P>
            Tribys opera íntegramente en los <strong>Estados Unidos</strong>.
            Nuestros servidores, nuestra base de datos, nuestra caché en tiempo
            real y nuestro almacenamiento de archivos están en el noroeste del
            Pacífico de EE. UU. (Oregón).
          </P>
          <P>
            Conviene señalar una excepción: la red de distribución de contenido que
            sirve las fotos las almacena en caché en ubicaciones de{' '}
            <strong>Norteamérica y Europa</strong>, por lo que una foto puede
            quedar temporalmente en caché en un servidor más cercano a quien la
            visualiza.
          </P>
          <P>
            Si usas Tribys desde México, tus datos personales se transfieren a los
            Estados Unidos y se tratan allí, bajo una legislación de protección de
            datos distinta de la mexicana. Al utilizar Tribys reconoces esta
            transferencia internacional. Nos apoyamos en cláusulas contractuales
            con cada uno de nuestros proveedores.
          </P>
        </>
      ),
    },
    {
      id: 'public',
      heading: '14. Datos que son públicos',
      body: (
        <>
          <P>Algunos datos son públicos por diseño. Tenlo presente.</P>
          <Ul
            items={[
              <><strong>Tu perfil</strong> —nombre de usuario, nombre, edad, género, foto, biografía, estado civil, tipo de personalidad, idiomas, intereses y artistas de Spotify— es visible para otros miembros y en una página web pública si alguien tiene el enlace a tu perfil. Tu correo electrónico, tu teléfono y tu ubicación nunca se muestran.</>,
              <><strong>Los lugares que creas</strong> son públicos, incluidas sus coordenadas exactas, y sus páginas pueden ser encontradas por buscadores.</>,
              <><strong>Las páginas de grupo</strong> son accesibles para cualquiera que tenga el enlace y muestran los datos del grupo y sus miembros, con nombres y fotos. Están marcadas para que los buscadores no las indexen.</>,
              <><strong>Vistas previas de enlaces.</strong> Cuando se comparte un enlace de Tribys en una aplicación de mensajería, esa aplicación obtiene una vista previa con el nombre y la foto. Cualquier servicio que lea el enlace puede verla.</>,
              <><strong>Los grupos próximos en tu perfil</strong> solo son visibles para tus amistades.</>,
            ]}
          />
          <P>
            Los enlaces de perfil y de grupo pueden regenerarse en la aplicación, lo
            que invalida de inmediato todas las copias del enlace anterior.
          </P>
        </>
      ),
    },
    {
      id: 'changes',
      heading: '15. Cambios a este aviso',
      body: (
        <P>
          Si modificamos este aviso actualizaremos la fecha que aparece arriba. Si
          un cambio afecta materialmente tus derechos o la forma en que usamos tus
          datos, te lo comunicaremos en la aplicación o por correo electrónico
          antes de que entre en vigor. No aplicaremos un uso materialmente distinto
          a los datos que ya nos hayas proporcionado sin tu consentimiento.
        </P>
      ),
    },
    {
      id: 'contact',
      heading: '16. Cómo contactarnos',
      body: (
        <>
          <P>
            Dudas y solicitudes de privacidad: <Tok value={entity.privacyEmail} />
            <br />
            Todo lo demás: <Tok value={entity.supportEmail} />
            <br />
            Por correo postal: <Tok value={entity.legalName} />,{' '}
            <Tok value={entity.address} />
          </P>
        </>
      ),
    },
    {
      id: 'mx',
      heading: 'Anexo A — México: derechos ARCO y finalidades',
      body: (
        <>
          <P>
            Este anexo aplica si te encuentras en México, conforme a la{' '}
            <em>
              Ley Federal de Protección de Datos Personales en Posesión de los
              Particulares
            </em>
            . Junto con las secciones anteriores, constituye el{' '}
            <strong>aviso de privacidad integral</strong>.
          </P>
          <H3>Finalidades necesarias y secundarias</H3>
          <P>
            <strong>Finalidades necesarias</strong>, sin las cuales el servicio no
            puede operar: crear y proteger tu cuenta; mostrarte grupos y lugares
            cercanos; la mensajería; las notificaciones sobre tu propia actividad;
            la seguridad y la moderación; el procesamiento de compras; y el
            cumplimiento de obligaciones legales.
          </P>
          <P>
            <strong>Finalidades secundarias</strong>, que puedes rechazar sin perder
            el acceso a Tribys: la verificación de identidad y el tratamiento
            biométrico que implica; la conexión con Spotify; y las notificaciones
            sobre grupos nuevos que coincidan con tus intereses. Puedes rechazarlas
            omitiendo la verificación, no conectando Spotify y desactivando ese tipo
            de notificación en Ajustes, o bien escribiendo a{' '}
            <Tok value={entity.privacyEmail} />.
          </P>
          <H3>Tus derechos ARCO</H3>
          <P>
            Tienes derecho al <strong>Acceso, Rectificación, Cancelación y
            Oposición</strong> respecto de tus datos personales, así como a{' '}
            <strong>revocar tu consentimiento</strong> y a limitar el uso o
            divulgación de tus datos. Su ejercicio es gratuito; únicamente podríamos
            recuperar los costos justificados de reproducción o envío.
          </P>
          <P>
            Envía tu solicitud ARCO a <Tok value={entity.privacyEmail} />{' '}
            indicando: tu nombre; un medio para comunicarte la respuesta; los
            documentos que acrediten tu identidad o la representación legal; una
            descripción clara de los datos respecto de los que ejerces el derecho; y
            cuál de los derechos deseas ejercer. Responderemos en un plazo de{' '}
            <strong>20 días hábiles</strong> y, si la solicitud procede, la haremos
            efectiva dentro de los <strong>15 días hábiles</strong> siguientes. El
            acceso, la rectificación y la cancelación también están disponibles de
            inmediato en la aplicación (sección 11).
          </P>
          <P>
            Tus datos se transfieren y almacenan en los Estados Unidos y se
            comparten con los proveedores enumerados en la sección 7. Si no estás
            conforme con nuestra respuesta, puedes presentar una queja ante la{' '}
            <strong>Secretaría Anticorrupción y Buen Gobierno</strong>, que asumió
            las funciones de protección de datos del extinto INAI en 2025.
          </P>
        </>
      ),
    },
    {
      id: 'us',
      heading: 'Anexo B — Estados Unidos',
      body: (
        <>
          <P>
            Si resides en un estado de EE. UU. con legislación de privacidad del
            consumidor —California, Virginia, Colorado, Connecticut, Utah, Texas,
            Oregón, Montana, Delaware, Iowa, Nebraska, Nuevo Hampshire, Nueva
            Jersey, Minnesota, Maryland, Tennessee, Indiana, Kentucky o Rhode
            Island— tienes derecho a: conocer qué datos recabamos, usamos y
            divulgamos y obtener una copia; obtener una copia portable; corregir
            datos inexactos; solicitar su eliminación; oponerte a la venta, al
            compartimiento, a la publicidad dirigida y a la elaboración de perfiles
            significativos; limitar el uso de datos personales sensibles; no sufrir
            discriminación por ejercer estos derechos; y apelar una negativa.
          </P>
          <P>
            No vendemos ni compartimos datos personales y no realizamos publicidad
            dirigida, por lo que no hay nada que desactivar en ese sentido; de todos
            modos respetamos las señales de exclusión como Global Privacy Control.
            El detalle por categorías legales, la forma de verificar tu identidad y
            los plazos de respuesta constan en el{' '}
            <a href={`${legalPaths.privacy}#us`}>Anexo A de la versión en inglés</a>
            , que mantenemos como texto de referencia para esas leyes estatales.
          </P>
        </>
      ),
    },
    {
      id: 'ca',
      heading: 'Anexo C — Canadá',
      body: (
        <>
          <P>
            Si te encuentras en Canadá, aplican la PIPEDA y, en Quebec, la{' '}
            <em>
              Ley sobre la protección de la información personal en el sector
              privado
            </em>{' '}
            reformada por la Ley 25.
          </P>
          <P>
            La persona responsable de la protección de datos personales en Tribys es
            el <strong>Responsable de Privacidad</strong> (
            <em>Privacy Officer</em>) de <Tok value={entity.legalName} />, con
            contacto en <Tok value={entity.privacyEmail} /> y domicilio en{' '}
            <Tok value={entity.address} />.
          </P>
          <P>
            Puedes retirar tu consentimiento respecto de cualquier función opcional
            en cualquier momento, y por completo eliminando tu cuenta. Tienes
            derecho de acceso y rectificación (secciones 11 y 12). La sección 6
            enumera todas nuestras decisiones automatizadas: cuando una decisión se
            tome de forma exclusivamente automatizada, puedes solicitar qué datos se
            usaron, con qué fin, y que una persona la revise. Tus datos se almacenan
            en los Estados Unidos y están sujetos a la legislación estadounidense,
            incluidas las solicitudes legítimas de acceso por parte de autoridades
            de ese país.
          </P>
          <P>
            Presenta primero tu queja ante nuestra persona responsable. Si no queda
            satisfecha, puedes acudir a la Oficina del Comisionado de Privacidad de
            Canadá o, en Quebec, a la{' '}
            <Ext href="https://www.cai.gouv.qc.ca/">
              Commission d&apos;accès à l&apos;information
            </Ext>
            .
          </P>
        </>
      ),
    },
  ],
};
